/**
 * envomart / WoodMart commerce utilities
 */
(function () {
  'use strict';

  const WISHLIST_KEY = 'envo_wishlist';
  const COMPARE_KEY = 'envo_compare';
  const RECENT_KEY = 'envo_recently_viewed';
  const MAX_RECENT = 12;

  function getStore(key) {
    try {
      return JSON.parse(localStorage.getItem(key) || '[]');
    } catch {
      return [];
    }
  }

  function setStore(key, arr) {
    localStorage.setItem(key, JSON.stringify(arr));
  }

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.Shopify?.theme?.moneyFormat || '${{amount}}');
    }
    return (cents / 100).toFixed(2);
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function renderRatingStars(rating) {
    const n = Math.max(0, Math.min(5, Math.round(Number(rating) || 5)));
    let html = '';
    for (let i = 1; i <= 5; i++) {
      html += i <= n ? '★' : '<span class="star-empty">★</span>';
    }
    return `<div class="wd-product-card__rating" aria-hidden="true">${html}</div>`;
  }

  function buildCompactCardHtml(product) {
    const variant = product.variants?.[0];
    if (!variant) return '';

    const img = product.images?.[0] || '';
    const handle = escapeHtml(product.handle);
    const title = escapeHtml(product.title);
    const url = `/products/${handle}`;

    let priceHtml = `<span class="wd-product-card__price-current">${formatMoney(variant.price)}</span>`;
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      priceHtml += `<span class="wd-product-card__price-compare">${formatMoney(variant.compare_at_price)}</span>`;
    }

    const imgTag = img
      ? `<img src="${escapeHtml(img)}" alt="${title}" width="80" height="80" loading="lazy">`
      : '';

    return `<div class="wd-product-card wd-product-card--compact">
      <div class="wd-product-card__media"><a href="${url}">${imgTag}</a></div>
      <div class="wd-product-card__body">
        <h3 class="wd-product-card__title"><a href="${url}">${title}</a></h3>
        ${renderRatingStars(5)}
        <div class="wd-product-card__price">${priceHtml}</div>
      </div>
    </div>`;
  }

  function getFreeShippingThreshold() {
    return window.envoCart?.freeShippingThreshold || 0;
  }

  const EnvoCommerce = {
    wishlist: getStore(WISHLIST_KEY),
    compare: getStore(COMPARE_KEY),

    init() {
      this.injectDrawer();
      this.injectQuickView();
      this.bindEvents();
      this.updateBadges();
      this.initScrollTop();
      this.initStickyHeader();
      this.initAccountPage();
      this.trackRecentlyViewed();
      this.initRecentlyViewed();
      this.initCountdowns();
      this.initProductCardGalleries();
      this.initMobileNav();
      this.initStickyNavMega();
      this.initSeoCollapsible();
      document.dispatchEvent(new CustomEvent('envo:ready'));
    },

    injectDrawer() {
      if (document.getElementById('wd-cart-drawer')) return;
      const el = document.createElement('div');
      el.id = 'wd-cart-drawer';
      el.className = 'wd-cart-drawer wd-cart-drawer--empty';
      el.innerHTML = `
        <div class="wd-cart-drawer__overlay" data-cart-close></div>
        <div class="wd-cart-drawer__panel">
          <div class="wd-cart-drawer__header">
            <h3>Shopping cart</h3>
            <button type="button" class="wd-cart-drawer__close" data-cart-close aria-label="Close cart">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              Close
            </button>
          </div>
          <div class="wd-cart-drawer__body">
            <div class="wd-cart-drawer__items" data-cart-items></div>
            <div class="wd-cart-drawer__empty" data-cart-empty hidden>
              <div class="wd-cart-drawer__empty-icon" aria-hidden="true">
                <svg width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/><path d="M9 9l1 6M15 9l-1 6"/></svg>
              </div>
              <p>No products in the cart.</p>
              <a href="${window.envoCart?.collectionsUrl || '/collections/all'}" class="wd-btn wd-btn--primary" data-cart-close>Return To Shop</a>
            </div>
          </div>
          <div class="wd-cart-drawer__footer" data-cart-footer hidden>
            <div class="wd-cart-drawer__shipping" data-cart-shipping hidden>
              <p data-cart-shipping-text></p>
              <div class="wd-cart-drawer__shipping-bar"><span data-cart-shipping-progress></span></div>
            </div>
            <div class="wd-cart-drawer__total">
              <span>Subtotal:</span>
              <span data-cart-subtotal>$0.00</span>
            </div>
            <a href="/cart" class="wd-btn wd-btn--cart-view">View cart</a>
            <a href="${window.envoCart?.checkoutUrl || '/checkout'}" class="wd-btn wd-btn--primary wd-btn--cart-checkout">Checkout</a>
          </div>
        </div>`;
      document.body.appendChild(el);
    },

    injectQuickView() {
      if (document.getElementById('wd-quick-view')) return;
      const el = document.createElement('div');
      el.id = 'wd-quick-view';
      el.className = 'wd-quick-view';
      el.innerHTML = `
        <div class="wd-quick-view__overlay" data-qv-close></div>
        <div class="wd-quick-view__modal">
          <button class="wd-quick-view__close" data-qv-close aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
          <div data-qv-content></div>
        </div>`;
      document.body.appendChild(el);
    },

    bindEvents() {
      document.addEventListener('click', (e) => {
        const productCard = e.target.closest('.wd-product-card:not(.wd-product-card--compact)');
        if (
          productCard &&
          !e.target.closest(
            'a, button, [data-add-to-cart], [data-compare-toggle], [data-quick-view], [data-wishlist-toggle]'
          )
        ) {
          const url = productCard.dataset.productUrl;
          if (url) {
            window.location.href = url;
            return;
          }
        }

        const cartOpen = e.target.closest('[data-cart-open]');
        if (cartOpen) {
          e.preventDefault();
          this.openCart();
        }

        const wishOpen = e.target.closest('[data-wishlist-open]');
        if (wishOpen) {
          e.preventDefault();
          window.location.href = window.envoCart?.wishlistUrl || '/pages/wishlist';
        }
        if (e.target.closest('[data-cart-close]')) {
          this.closeCart();
        }
        if (e.target.closest('[data-qv-close]')) {
          this.closeQuickView();
        }

        const wishBtn = e.target.closest('[data-wishlist-toggle]');
        if (wishBtn) {
          e.preventDefault();
          this.toggleWishlist(wishBtn.dataset.handle, wishBtn);
        }

        const compareBtn = e.target.closest('[data-compare-toggle]');
        if (compareBtn) {
          e.preventDefault();
          this.toggleCompare(compareBtn.dataset.handle, compareBtn);
        }

        const qvBtn = e.target.closest('[data-quick-view]');
        if (qvBtn) {
          e.preventDefault();
          this.openQuickView(qvBtn.dataset.handle);
        }

        const atcBtn = e.target.closest('[data-add-to-cart]');
        if (atcBtn && !atcBtn.disabled) {
          e.preventDefault();
          this.addToCart(atcBtn);
        }

        const qtyMinus = e.target.closest('[data-cart-qty-minus]');
        if (qtyMinus) {
          e.preventDefault();
          this.changeLineQuantity(qtyMinus.dataset.key, Number(qtyMinus.dataset.qty) - 1);
        }

        const qtyPlus = e.target.closest('[data-cart-qty-plus]');
        if (qtyPlus) {
          e.preventDefault();
          this.changeLineQuantity(qtyPlus.dataset.key, Number(qtyPlus.dataset.qty) + 1);
        }

        const removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
          e.preventDefault();
          this.changeLineQuantity(removeBtn.dataset.key, 0);
        }

        const stickyToggle = e.target.closest('[data-sticky-nav-toggle]');
        if (stickyToggle) {
          e.preventDefault();
          const nav = document.getElementById('wd-sticky-nav');
          if (window.matchMedia('(min-width: 1025px)').matches && nav) {
            nav.classList.toggle('wd-opened');
            this.syncStickyNavOverlay();
          } else if (document.body.classList.contains('wd-mobile-nav-open')) {
            this.closeMobileNav();
          } else {
            this.openMobileNav('categories');
          }
          return;
        }

        const nav = document.getElementById('wd-sticky-nav');
        if (
          nav &&
          nav.classList.contains('wd-opened') &&
          !e.target.closest('#wd-sticky-nav') &&
          !e.target.closest('[data-sticky-nav-toggle]') &&
          !e.target.closest('#wd-sticky-nav-overlay')
        ) {
          nav.classList.remove('wd-opened');
          this.syncStickyNavOverlay();
        }

        if (e.target.closest('#wd-sticky-nav-overlay')) {
          nav?.classList.remove('wd-opened');
          this.syncStickyNavOverlay();
        }

        const mobileMenu = e.target.closest('[data-mobile-menu-toggle]');
        if (mobileMenu) {
          e.preventDefault();
          const defaultTab = mobileMenu.dataset.mobileNavDefault || 'menu';
          if (document.body.classList.contains('wd-mobile-nav-open')) {
            this.closeMobileNav();
          } else {
            this.openMobileNav(defaultTab);
          }
          return;
        }

        const accountOpen = e.target.closest('[data-account-open]');
        if (accountOpen) {
          e.preventDefault();
          this.closeMobileNav();
          this.openAccountSidebar();
          return;
        }

        if (e.target.closest('[data-account-close]')) {
          e.preventDefault();
          this.closeAccountSidebar();
        }

        const accountSwitch = e.target.closest('[data-account-switch]');
        if (accountSwitch) {
          e.preventDefault();
          this.switchAccountSidebar(accountSwitch.dataset.accountSwitch);
        }

        const switchAuth = e.target.closest('[data-switch-auth]');
        if (switchAuth) {
          e.preventDefault();
          const tabs = document.querySelector('[data-account-tabs]');
          if (!tabs) return;
          if (switchAuth.dataset.switchAuth === 'register') {
            tabs.classList.add('wd-active-register');
          } else {
            tabs.classList.remove('wd-active-register');
          }
        }

        if (e.target.closest('[data-show-recover]')) {
          e.preventDefault();
          const tabs = document.querySelector('[data-account-tabs]');
          if (tabs) {
            tabs.classList.add('is-recover');
            document.getElementById('recover')?.classList.add('is-visible');
          }
        }

        if (e.target.closest('[data-hide-recover]')) {
          e.preventDefault();
          const tabs = document.querySelector('[data-account-tabs]');
          if (tabs) {
            tabs.classList.remove('is-recover');
            document.getElementById('recover')?.classList.remove('is-visible');
          }
        }

        const pwdToggle = e.target.closest('[data-password-toggle]');
        if (pwdToggle) {
          e.preventDefault();
          const input = pwdToggle.closest('.wd-password-wrap')?.querySelector('input');
          if (!input) return;
          const isText = input.type === 'text';
          input.type = isText ? 'password' : 'text';
          pwdToggle.setAttribute('aria-label', isText ? 'Show password' : 'Hide password');
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeCart();
          this.closeQuickView();
          this.closeAccountSidebar();
        }
      });

      document.addEventListener('cart:updated', () => this.refreshCart());
    },

    async refreshCart() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
        });
        document.querySelectorAll('[data-cart-total]').forEach((el) => {
          el.textContent = formatMoney(cart.total_price);
        });
        this.renderCartItems(cart);
        return cart;
      } catch (err) {
        console.error('Cart refresh failed', err);
      }
    },

    renderCartItems(cart) {
      const drawer = document.getElementById('wd-cart-drawer');
      const container = document.querySelector('[data-cart-items]');
      const emptyState = document.querySelector('[data-cart-empty]');
      const footer = document.querySelector('[data-cart-footer]');
      if (!container) return;

      const isEmpty = !cart.items.length;
      drawer?.classList.toggle('wd-cart-drawer--empty', isEmpty);

      if (emptyState) emptyState.hidden = !isEmpty;
      if (footer) footer.hidden = isEmpty;
      container.hidden = isEmpty;

      if (isEmpty) {
        container.innerHTML = '';
        return;
      }

      container.innerHTML = cart.items
        .map(
          (item) => `
        <div class="wd-cart-drawer__item" data-cart-line="${item.key}">
          <a href="${item.url}" class="wd-cart-drawer__item-image">
            <img src="${item.image}" alt="${escapeHtml(item.title)}" width="80" height="80" loading="lazy">
          </a>
          <div class="wd-cart-drawer__item-body">
            <div class="wd-cart-drawer__item-top">
              <a href="${item.url}" class="wd-cart-drawer__item-title">${escapeHtml(item.product_title)}</a>
              <button type="button" class="wd-cart-drawer__item-remove" data-cart-remove data-key="${item.key}" aria-label="Remove item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            ${item.sku ? `<div class="wd-cart-drawer__item-sku">${escapeHtml(item.sku)}</div>` : ''}
            <div class="wd-cart-drawer__item-meta">
              <div class="wd-cart-drawer__qty">
                <button type="button" class="wd-cart-drawer__qty-btn" data-cart-qty-minus data-key="${item.key}" data-qty="${item.quantity}" aria-label="Decrease quantity">−</button>
                <input type="text" value="${item.quantity}" readonly aria-label="Quantity">
                <button type="button" class="wd-cart-drawer__qty-btn" data-cart-qty-plus data-key="${item.key}" data-qty="${item.quantity}" aria-label="Increase quantity">+</button>
              </div>
              <div class="wd-cart-drawer__item-price">
                <span class="wd-cart-drawer__item-qty-label">${item.quantity} ×</span>
                <span class="wd-cart-drawer__item-unit-price">${formatMoney(item.final_price)}</span>
              </div>
            </div>
          </div>
        </div>`
        )
        .join('');

      const subtotal = document.querySelector('#wd-cart-drawer [data-cart-subtotal]');
      if (subtotal) subtotal.textContent = formatMoney(cart.total_price);

      this.renderShippingBar(cart.total_price);
    },

    renderShippingBar(cartTotal) {
      const threshold = getFreeShippingThreshold();
      const wrap = document.querySelector('[data-cart-shipping]');
      const text = document.querySelector('[data-cart-shipping-text]');
      const progress = document.querySelector('[data-cart-shipping-progress]');
      if (!wrap || !text || !progress || !threshold) {
        wrap?.setAttribute('hidden', '');
        return;
      }

      wrap.hidden = false;
      if (cartTotal >= threshold) {
        text.textContent = 'Your order qualifies for free shipping!';
        progress.style.width = '100%';
        return;
      }

      const remaining = threshold - cartTotal;
      text.textContent = `Add ${formatMoney(remaining)} to cart and get free shipping!`;
      progress.style.width = `${Math.min(100, (cartTotal / threshold) * 100)}%`;
    },

    async changeLineQuantity(key, quantity) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity }),
        });
        if (!res.ok) throw new Error('Update failed');
        await this.refreshCart();
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } catch (err) {
        console.error('Cart update failed', err);
      }
    },

    async openCart() {
      await this.refreshCart();
      document.getElementById('wd-cart-drawer')?.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    },

    closeCart() {
      document.getElementById('wd-cart-drawer')?.classList.remove('is-open');
      document.body.style.overflow = '';
    },

    async addToCart(btn) {
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      const original = btn.textContent;
      btn.classList.add('wd-btn--loading');
      btn.disabled = true;
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        if (!res.ok) throw new Error('Add failed');
        btn.textContent = 'Added!';
        await this.refreshCart();
        document.dispatchEvent(new CustomEvent('cart:updated'));
        setTimeout(() => this.openCart(), 300);
      } catch {
        btn.textContent = 'Error';
      } finally {
        setTimeout(() => {
          btn.textContent = original;
          btn.classList.remove('wd-btn--loading');
          btn.disabled = false;
        }, 1500);
      }
    },

    toggleWishlist(handle, btn) {
      const list = getStore(WISHLIST_KEY);
      const idx = list.indexOf(handle);
      if (idx > -1) {
        list.splice(idx, 1);
        btn?.classList.remove('is-active');
      } else {
        list.push(handle);
        btn?.classList.add('is-active');
      }
      setStore(WISHLIST_KEY, list);
      this.wishlist = list;
      this.updateBadges();
      document.dispatchEvent(new CustomEvent('envo:wishlist-updated', { detail: { list } }));
    },

    toggleCompare(handle, btn) {
      const list = getStore(COMPARE_KEY);
      const idx = list.indexOf(handle);
      if (idx > -1) {
        list.splice(idx, 1);
        btn?.classList.remove('is-active');
      } else {
        if (list.length >= 4) return;
        list.push(handle);
        btn?.classList.add('is-active');
      }
      setStore(COMPARE_KEY, list);
      this.compare = list;
      this.updateBadges();
    },

    updateBadges() {
      const wishCount = getStore(WISHLIST_KEY).length;
      const compareCount = getStore(COMPARE_KEY).length;
      document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
        el.textContent = wishCount;
        el.style.display = wishCount ? 'flex' : 'none';
      });
      document.querySelectorAll('[data-compare-count]').forEach((el) => {
        el.textContent = compareCount;
        el.style.display = compareCount ? 'flex' : 'none';
      });
      document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
        if (getStore(WISHLIST_KEY).includes(btn.dataset.handle)) {
          btn.classList.add('is-active');
        }
      });
      document.querySelectorAll('[data-compare-toggle]').forEach((btn) => {
        if (getStore(COMPARE_KEY).includes(btn.dataset.handle)) {
          btn.classList.add('is-active');
        }
      });
    },

    async openQuickView(handle) {
      const modal = document.getElementById('wd-quick-view');
      const content = modal?.querySelector('[data-qv-content]');
      if (!modal || !content) return;
      this.addToRecentlyViewed(handle);
      content.innerHTML = '<p style="text-align:center;padding:40px;">Loading...</p>';
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      try {
        const res = await fetch(`/products/${handle}.js`);
        const product = await res.json();
        const variant = product.variants[0];
        const img = product.images[0] || '';
        content.innerHTML = `
          <div class="wd-quick-view__grid">
            <div><img src="${img}" alt="${product.title}" style="width:100%;border-radius:8px;"></div>
            <div>
              <h2 style="font-family:var(--wd-font-title);margin:0 0 12px;">${product.title}</h2>
              <div style="font-size:20px;font-weight:700;color:var(--wd-primary);margin-bottom:16px;">${formatMoney(variant.price)}</div>
              <div style="margin-bottom:20px;color:#777;">${product.description?.substring(0, 200) || ''}...</div>
              ${
                product.variants.length === 1
                  ? `<button class="wd-btn wd-btn--primary" data-add-to-cart data-variant-id="${variant.id}">Add to Cart</button>`
                  : `<a href="/products/${handle}" class="wd-btn wd-btn--primary">Select Options</a>`
              }
              <a href="/products/${handle}" style="display:block;margin-top:12px;color:var(--wd-primary);">View full details</a>
            </div>
          </div>`;
      } catch {
        content.innerHTML = '<p>Could not load product.</p>';
      }
    },

    closeQuickView() {
      document.getElementById('wd-quick-view')?.classList.remove('is-open');
      document.body.style.overflow = '';
    },

    addToRecentlyViewed(handle) {
      if (!handle) return;
      let list = getStore(RECENT_KEY).filter((h) => h !== handle);
      list.unshift(handle);
      setStore(RECENT_KEY, list.slice(0, MAX_RECENT));
    },

    trackRecentlyViewed() {
      if (window.meta?.product?.handle) {
        this.addToRecentlyViewed(window.meta.product.handle);
      }
    },

    getRecentlyViewed() {
      return getStore(RECENT_KEY);
    },

    async initRecentlyViewed() {
      const sections = document.querySelectorAll('[data-recently-viewed-section]');
      if (!sections.length) return;

      const handles = this.getRecentlyViewed().filter(Boolean);
      if (!handles.length) return;

      for (const section of sections) {
        const wrapper = section.querySelector('[data-recent-products]');
        if (!wrapper) continue;

        const slides = [];
        for (const handle of handles) {
          try {
            const res = await fetch(`/products/${handle}.js`);
            if (!res.ok) continue;
            const product = await res.json();
            const card = buildCompactCardHtml(product);
            if (card) slides.push(`<div class="swiper-slide">${card}</div>`);
          } catch {
            /* skip unavailable products */
          }
        }

        if (!slides.length) continue;

        wrapper.innerHTML = slides.join('');
        section.removeAttribute('hidden');

        const slider = section.querySelector('[data-recent-slider]');
        if (slider && typeof Swiper !== 'undefined' && !slider.swiper) {
          this.initSwiper(slider, {
            slidesPerView: 2,
            spaceBetween: 16,
            navigation: {
              nextEl: slider.querySelector('.swiper-button-next'),
              prevEl: slider.querySelector('.swiper-button-prev'),
            },
            breakpoints: {
              768: { slidesPerView: 3 },
              1024: { slidesPerView: 4 },
              1280: { slidesPerView: 5 },
            },
          });
        }
      }
    },

    initCountdowns() {
      document.querySelectorAll('[data-countdown]').forEach((el) => {
        const target = new Date(el.dataset.countdown).getTime();
        const tick = () => {
          const diff = Math.max(0, target - Date.now());
          const d = Math.floor(diff / 86400000);
          const h = Math.floor((diff % 86400000) / 3600000);
          const m = Math.floor((diff % 3600000) / 60000);
          const s = Math.floor((diff % 60000) / 1000);
          el.querySelector('[data-cd-days]').textContent = String(d).padStart(2, '0');
          el.querySelector('[data-cd-hours]').textContent = String(h).padStart(2, '0');
          el.querySelector('[data-cd-mins]').textContent = String(m).padStart(2, '0');
          el.querySelector('[data-cd-secs]').textContent = String(s).padStart(2, '0');
        };
        tick();
        setInterval(tick, 1000);
      });
    },

    initProductCardGalleries() {
      const CYCLE_INTERVAL = 2500;

      document.querySelectorAll('.wd-product-card__media--gallery').forEach((media) => {
        const card = media.closest('.wd-product-card');
        const imgs = media.querySelectorAll('.wd-product-card__media-img');
        if (!card || imgs.length < 2) return;

        imgs.forEach((img, index) => {
          img.classList.toggle('is-active', index === 0);
        });

        let cycleTimer = null;
        let current = 0;

        const setActive = (index) => {
          imgs.forEach((img, i) => img.classList.toggle('is-active', i === index));
          current = index;
        };

        const clearTimers = () => {
          clearInterval(cycleTimer);
          cycleTimer = null;
        };

        card.addEventListener('mouseenter', () => {
          clearTimers();
          setActive((current + 1) % imgs.length);
          if (imgs.length > 2) {
            cycleTimer = setInterval(() => {
              setActive((current + 1) % imgs.length);
            }, CYCLE_INTERVAL);
          }
        });

        card.addEventListener('mouseleave', () => {
          clearTimers();
          setActive(0);
        });
      });
    },

    initScrollTop() {
      let btn = document.querySelector('.wd-scroll-top');
      if (!btn) {
        btn = document.createElement('button');
        btn.className = 'wd-scroll-top';
        btn.type = 'button';
        btn.setAttribute('aria-label', 'Scroll to top');
        btn.innerHTML =
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 19V5M5 12l7-7 7 7"/></svg>';
        const stack = document.querySelector('.wd-float-actions');
        if (stack) stack.appendChild(btn);
        else document.body.appendChild(btn);
      }
      window.addEventListener('scroll', () => {
        btn.classList.toggle('is-visible', window.scrollY > 400);
      });
      btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
    },

    initStickyHeader() {
      const header = document.querySelector('.wd-header');
      if (!header) return;
      let lastY = 0;
      window.addEventListener('scroll', () => {
        const y = window.scrollY;
        if (y > 100) {
          header.classList.add('is-sticky');
          document.body.classList.add('wd-header-sticky-active');
          if (y > lastY) header.classList.add('is-hidden-general');
          else header.classList.remove('is-hidden-general');
        } else {
          header.classList.remove('is-sticky', 'is-hidden-general');
          document.body.classList.remove('wd-header-sticky-active');
        }
        lastY = y;
      });
    },

    openAccountSidebar() {
      const sidebar = document.getElementById('wd-account-sidebar');
      if (!sidebar) return;
      this.switchAccountSidebar('login');
      document.body.classList.add('wd-account-open');
      sidebar.setAttribute('aria-hidden', 'false');
    },

    switchAccountSidebar(view) {
      const sidebar = document.getElementById('wd-account-sidebar');
      if (!sidebar) return;

      sidebar.querySelectorAll('[data-account-panel]').forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.accountPanel === view);
      });

      sidebar.querySelectorAll('[data-account-sidebar-cta]').forEach((cta) => {
        cta.classList.toggle('wd-hide', cta.dataset.accountSidebarCta !== view);
      });

      const title = sidebar.querySelector('[data-account-sidebar-title]');
      if (title) {
        title.textContent = view === 'register' ? 'Create account' : 'Sign in';
      }

      sidebar.setAttribute(
        'aria-label',
        view === 'register' ? 'Create account' : 'Sign in'
      );
    },

    closeAccountSidebar() {
      document.body.classList.remove('wd-account-open');
      const sidebar = document.getElementById('wd-account-sidebar');
      if (sidebar) sidebar.setAttribute('aria-hidden', 'true');
    },

    initAccountPage() {
      const tabs = document.querySelector('[data-account-tabs]');
      if (!tabs) return;

      if (window.location.hash === '#recover') {
        tabs.classList.add('is-recover');
        document.getElementById('recover')?.classList.add('is-visible');
      }

      if (window.location.pathname.includes('/register')) {
        tabs.classList.add('wd-active-register');
      }
    },

    initStickyNavMega() {
      const nav = document.getElementById('wd-sticky-nav');
      if (!nav) return;

      const sync = () => this.syncStickyNavOverlay();
      nav.addEventListener('mouseenter', sync);
      nav.addEventListener('mouseleave', sync);
      nav.addEventListener('focusin', sync);
      nav.addEventListener('focusout', () => setTimeout(sync, 0));
    },

    syncStickyNavOverlay() {
      const nav = document.getElementById('wd-sticky-nav');
      const open = !!(nav && (nav.matches(':hover') || nav.classList.contains('wd-opened')));
      document.body.classList.toggle('wd-sticky-nav-mega-open', open);
      const overlay = document.getElementById('wd-sticky-nav-overlay');
      if (overlay) {
        if (open) overlay.removeAttribute('hidden');
        else overlay.setAttribute('hidden', '');
      }
    },

    initMobileNav() {
      const nav = document.getElementById('wd-mobile-nav');
      if (!nav) return;

      this.populateMobileNavCategories();

      nav.addEventListener('click', (e) => {
        const tab = e.target.closest('[data-mobile-nav-tab]');
        if (tab) {
          e.preventDefault();
          this.setMobileNavTab(tab.dataset.mobileNavTab);
          return;
        }

        const openSub = e.target.closest('[data-mobile-nav-open-submenu]');
        if (openSub) {
          e.preventDefault();
          const panel = nav.querySelector(`[data-mobile-nav-submenu="${openSub.dataset.mobileNavOpenSubmenu}"]`);
          if (panel) {
            panel.removeAttribute('hidden');
            requestAnimationFrame(() => panel.classList.add('is-open'));
          }
          return;
        }

        if (e.target.closest('[data-mobile-nav-back]')) {
          e.preventDefault();
          const panel = e.target.closest('.wd-mobile-nav__submenu');
          if (panel) {
            panel.classList.remove('is-open');
            setTimeout(() => panel.setAttribute('hidden', ''), 300);
          }
        }

        const link = e.target.closest('.wd-mobile-nav__link[href]');
        if (link && link.getAttribute('href') !== '#') {
          this.closeMobileNav();
        }
      });
    },

    populateMobileNavCategories() {
      const list = document.querySelector('[data-mobile-nav-categories][data-mobile-nav-sync-sticky]');
      const stickyNav = document.getElementById('wd-sticky-nav');
      const subRoot = document.querySelector('[data-mobile-nav-submenus-root]');
      if (!list || !stickyNav) return;

      const items = stickyNav.querySelectorAll('.wd-sticky-nav-item');
      if (!items.length) return;

      list.innerHTML = '';
      if (subRoot) subRoot.innerHTML = '';

      items.forEach((item, index) => {
        const link = item.querySelector('a.wd-sticky-nav-link');
        const label = item.querySelector('.wd-sticky-nav-text')?.textContent?.trim();
        if (!link || !label) return;

        const iconHtml = item.querySelector('.wd-sticky-nav-icon')?.innerHTML || '';
        const megaLinks = item.querySelectorAll('.wd-sticky-nav-mega__list a');
        const submenuId = `sticky-cat-${index}`;
        const li = document.createElement('li');
        li.className = 'wd-mobile-nav__item';

        if (megaLinks.length && subRoot) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'wd-mobile-nav__link wd-mobile-nav__link--parent';
          btn.dataset.mobileNavOpenSubmenu = submenuId;
          btn.innerHTML = `
            <span class="wd-mobile-nav__icon">${iconHtml}</span>
            <span class="wd-mobile-nav__label"></span>
            <span class="wd-mobile-nav__chevron" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
            </span>`;
          btn.querySelector('.wd-mobile-nav__label').textContent = label;
          li.append(btn);

          const panel = document.createElement('div');
          panel.className = 'wd-mobile-nav__submenu';
          panel.dataset.mobileNavSubmenu = submenuId;
          panel.hidden = true;

          let subHtml = `
            <div class="wd-mobile-nav__submenu-head">
              <button type="button" class="wd-mobile-nav__back" data-mobile-nav-back aria-label="Back to categories">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
                Back to categories
              </button>
            </div>
            <ul class="wd-mobile-nav__list">
              <li class="wd-mobile-nav__item">
                <a href="${link.getAttribute('href')}" class="wd-mobile-nav__link wd-mobile-nav__link--all">
                  <span class="wd-mobile-nav__label">All ${label}</span>
                </a>
              </li>`;

          const cols = item.querySelectorAll('.wd-sticky-nav-mega__col');
          const linkGroups = cols.length ? cols : [item];

          linkGroups.forEach((group) => {
            const headingEl = group.querySelector('.wd-sticky-nav-mega__heading');
            const heading = headingEl ? headingEl.textContent.trim() : '';
            if (heading && cols.length) {
              subHtml += `
                <li class="wd-mobile-nav__item wd-mobile-nav__item--group">
                  <span class="wd-mobile-nav__group-label">${heading}</span>
                </li>`;
            }

            const links = group.querySelectorAll('.wd-sticky-nav-mega__list a');
            links.forEach((sub) => {
              const subLabel = sub.dataset.subLabel || sub.textContent.trim();
              subHtml += `
                <li class="wd-mobile-nav__item">
                  <a href="${sub.getAttribute('href')}" class="wd-mobile-nav__link">
                    <span class="wd-mobile-nav__label">${subLabel}</span>
                  </a>
                </li>`;
            });
          });

          subHtml += '</ul>';
          panel.innerHTML = subHtml;
          subRoot.append(panel);
        } else {
          const anchor = document.createElement('a');
          anchor.href = link.getAttribute('href');
          anchor.className = 'wd-mobile-nav__link';
          anchor.innerHTML = `<span class="wd-mobile-nav__icon">${iconHtml}</span><span class="wd-mobile-nav__label"></span>`;
          anchor.querySelector('.wd-mobile-nav__label').textContent = label;
          li.append(anchor);
        }

        list.append(li);
      });
    },

    setMobileNavTab(tabName) {
      const nav = document.getElementById('wd-mobile-nav');
      if (!nav) return;

      nav.querySelectorAll('[data-mobile-nav-tab]').forEach((tab) => {
        const active = tab.dataset.mobileNavTab === tabName;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      nav.querySelectorAll('[data-mobile-nav-panel]').forEach((panel) => {
        const active = panel.dataset.mobileNavPanel === tabName;
        panel.classList.toggle('is-active', active);
        if (active) panel.removeAttribute('hidden');
        else panel.setAttribute('hidden', '');
      });

      nav.querySelectorAll('.wd-mobile-nav__submenu.is-open').forEach((panel) => {
        panel.classList.remove('is-open');
        panel.setAttribute('hidden', '');
      });
    },

    openMobileNav(defaultTab) {
      this.populateMobileNavCategories();
      this.setMobileNavTab(defaultTab || 'categories');
      document.body.classList.add('wd-mobile-nav-open');
    },

    closeMobileNav() {
      document.body.classList.remove('wd-mobile-nav-open');
      const nav = document.getElementById('wd-mobile-nav');
      nav?.querySelectorAll('.wd-mobile-nav__submenu.is-open').forEach((panel) => {
        panel.classList.remove('is-open');
        panel.setAttribute('hidden', '');
      });
    },

    initSeoCollapsible() {
      document.querySelectorAll('[data-seo-collapsible]').forEach((block) => {
        const btn = block.querySelector('[data-seo-toggle]');
        const more = block.querySelector('[data-seo-more]');
        if (!btn || !more) return;

        btn.addEventListener('click', () => {
          const expanded = block.classList.toggle('is-expanded');
          if (expanded) {
            more.removeAttribute('hidden');
          } else {
            more.setAttribute('hidden', '');
          }
          btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
          btn.textContent = expanded ? btn.dataset.labelLess : btn.dataset.labelMore;
        });
      });
    },

    initSwiper(container, options) {
      if (typeof Swiper === 'undefined') return null;
      return new Swiper(container, options);
    },
  };

  window.EnvoCommerce = EnvoCommerce;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      EnvoCommerce.init();
      EnvoCommerce.refreshCart();
    });
  } else {
    EnvoCommerce.init();
    EnvoCommerce.refreshCart();
  }
})();
